'use client';

import { useEffect, useState } from 'react';
import { Heading, Text, Button, Link as EditorialLink } from '@savage-ds/primitives';
import { Field, Input, Radio, RadioGroup } from '@savage-ds/forms';
import { Alert } from '@savage-ds/feedback';

/**
 * API route that accepts the signup POST. Must stamp
 * `source: 'reporting-maximizer-saas'` server-side (see the route handler).
 */
const SUBMIT_TO = '/reporting/api/reporting-maximizer/signup';
const PLANS_FROM = '/reporting/api/billing/plans';

interface PlanOption {
  planKey: string | null;
  name: string;
  tierLabel: string;
  amountCents: number;
  currency: string;
  billingInterval: string;
  requiresPayment: boolean;
}

function formatPlanPrice(plan: PlanOption): string {
  const amount = (plan.amountCents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: (plan.currency || 'usd').toUpperCase(),
    maximumFractionDigits: 0,
  });
  return `${amount}/${plan.billingInterval === 'year' ? 'yr' : 'mo'}`;
}

export default function SignupForm() {
  const [email, setEmail] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [planKey, setPlanKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(PLANS_FROM)
      .then((res) => (res.ok ? res.json() : { plans: [] }))
      .then((body: { plans?: PlanOption[] }) => {
        if (cancelled) return;
        const loaded = (body.plans ?? []).filter((plan) => plan.planKey);
        setPlans(loaded);
        // Default selection mirrors the server default (cheapest active plan).
        if (loaded.length > 0) setPlanKey(loaded[0].planKey);
      })
      .catch(() => {
        // Picker is progressive enhancement: signup still works with the
        // server-side default plan when the catalog fetch fails.
      });
    return () => { cancelled = true; };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!email.trim()) { setError('Please enter your email address.'); return; }
    if (!organizationName.trim()) { setError('Please enter your organization name.'); return; }
    if (!password) { setError('Please enter a password.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }

    setLoading(true);
    setError('');

    const res = await fetch(SUBMIT_TO, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email.trim(),
        password,
        organizationName: organizationName.trim(),
        referralCode: referralCode.trim() || undefined,
        planKey: planKey ?? undefined,
      }),
    });

    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      redirectTo?: string;
      requiresPayment?: boolean;
    };

    if (!res.ok) {
      setError(body.error ?? 'Signup failed. Please try again.');
      setLoading(false);
      return;
    }

    // Paid plans: hand the customer straight to the Stripe-hosted checkout the
    // server minted (entitlement stays disabled until the webhook confirms).
    if (body.requiresPayment && body.redirectTo && body.redirectTo.startsWith('https://')) {
      window.location.assign(body.redirectTo);
      return;
    }

    setLoading(false);
    setDone(true);
  }

  if (done) {
    return (
      <>
        <Heading level={1}>Account <em>created</em></Heading>
        <Text variant="muted" className="ds-auth-lede">
          Check your email to confirm your address, then sign in.
        </Text>
        <Text variant="small" className="ds-auth-alt">
          <EditorialLink href="/reporting/login">Go to sign in</EditorialLink>
        </Text>
      </>
    );
  }

  return (
    <>
      <Heading level={1}>Create your <em>account</em></Heading>
      <Text variant="muted" className="ds-auth-lede">
        Reporting Maximizer
      </Text>

      <div className="ds-auth-manual">
        <form onSubmit={handleSubmit} noValidate>
          {plans.length > 0 && (
            <Field label="Plan">
              <RadioGroup label="Plan">
                {plans.map((plan) => (
                  <Radio
                    key={plan.planKey}
                    name="signup-plan"
                    value={plan.planKey ?? ''}
                    checked={plan.planKey === planKey}
                    onChange={() => setPlanKey(plan.planKey)}
                    description={formatPlanPrice(plan)}
                  >
                    {plan.tierLabel}
                  </Radio>
                ))}
              </RadioGroup>
            </Field>
          )}

          <Field label="Email address">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
            />
          </Field>

          <Field label="Organization name">
            <Input
              type="text"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              placeholder="Acme Inc."
              autoComplete="organization"
            />
          </Field>

          <Field label="Password">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              autoComplete="new-password"
            />
          </Field>

          <Field label="Confirm password">
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              autoComplete="new-password"
            />
          </Field>

          <Field label="Referral code" optional>
            <Input
              type="text"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
              placeholder="Enter code if you have one"
              autoComplete="off"
            />
          </Field>

          {error && <Alert tone="danger">{error}</Alert>}

          <Button type="submit" variant="primary" size="full" loading={loading} disabled={loading}>
            {loading ? 'Creating account…' : 'Create account'}
          </Button>
        </form>
      </div>

      <Text variant="small" className="ds-auth-alt">
        Already have an account? <EditorialLink href="/reporting/login">Sign in</EditorialLink>
      </Text>
    </>
  );
}
